import { useCallback, useMemo } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { DrawData, SettingsProps } from '../draw.constants';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CONTENT_IMAGE } from 'global/constants';
import {
  MultiView,
  MultiViewPage,
} from 'approot/shared/layout/layout-multi-view';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import { Box } from 'approot/shared/design-system/box/box.style';
import ImageUploadField from '../../shared/image-upload-field';
import {
  useAddImageToComponent,
  useFindComponentDimensions,
} from '../../component.hooks';
import ComponentImageField from '../../shared/component-image-field/component-image-field';
import ComponentImageModal from '../../shared/component-image-modal/component-image-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { COMPONENT_DIMENSION_ID_PREFIX } from '../../shared/component-height/component-dimensions.constants';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';
import { PStyled } from 'approot/shared/styles/typography.style';
import ActivitySheet from '../../shared/sheet/activity-sheet';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import EditDrawCanvas from './edit-draw-canvas';
import { DrawSettingsCanvasWrapper } from './draw-settings.style';
import { ActivitySampleBanner } from '../../sample/activity-sample-banner.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';

type Props = SettingsProps & {
  onCloseChildDrawer?: () => void;
};

const DrawSettings = ({
  id,
  sectionId,
  contentId,
  properties,
  onCloseChildDrawer,
}: Props) => {
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const {
    isVisible: isCustomDrawVisible,
    openModal: openCustomDraw,
    closeModal: closeCustomDraw,
  } = useSimpleModalState();

  const { closeDrawer } = useActivityBuilderContext();
  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const { contentType } = useGetInteractiveLessonParams();
  const { addImageToComponent } = useAddImageToComponent();
  const { elementRef, elementDimensionLabel } = useFindComponentDimensions(
    id,
    COMPONENT_DIMENSION_ID_PREFIX
  );

  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const handleImageUpload = useCallback(
    (key: string, fileName: string) => {
      const requestId = createRequest(id);
      addImageToComponent({
        variables: {
          input: {
            contentId,
            contentType,
            sectionId,
            componentId: id,
            imageInput: {
              key,
              imageType: CONTENT_IMAGE,
              data: {
                designId: null,
              },
              fileName,
            },
          },
        },
      }).finally(() => {
        clearRequest(requestId);
      });
    },
    [
      addImageToComponent,
      id,
      contentId,
      sectionId,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  const image = useMemo(
    () =>
      properties.image
        ? {
            key: properties.image.key,
            original: properties.image.original,
            fileName: properties.image.fileName,
            designId: null,
          }
        : null,
    [properties]
  );

  const handleSaveAndClose = useCallback(
    (drawingData: DrawData | null) => {
      const payload = drawingData
        ? {
            __typename: 'DrawQuestionMetadata' as const,
            data: drawingData.data,
            originalWidth: drawingData.originalWidth || 0,
            originalHeight: drawingData.originalHeight || 0,
            updatedAt: new Date().toISOString(),
          }
        : null;
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              draw: payload,
            },
          },
        },
        properties: {
          ...properties,
          draw: payload,
        },
        typename: 'ActivitiesDrawComponent',
      });

      closeCustomDraw();
    },
    [id, sectionId, properties, updateComponentProperties, closeCustomDraw]
  );

  return (
    <MultiView>
      {multiviewProps => (
        <>
          <MultiViewPage
            index={0}
            {...multiviewProps}
            onClose={onCloseChildDrawer ? onCloseChildDrawer : closeDrawer}
            renderItem={() => (
              <ActivityBuilderDrawerWrapper>
                <ActivityBuilderDrawerHeader
                  isBack={!!onCloseChildDrawer}
                  title="Drawing"
                  onClose={
                    onCloseChildDrawer ? onCloseChildDrawer : closeDrawer
                  }
                />
                <ActivityBuilderDrawerContent>
                  {isVisible && elementRef && (
                    <ComponentImageModal
                      loading={loading}
                      containerDimensions={{
                        width: elementRef.offsetWidth,
                        height: elementRef.offsetHeight,
                      }}
                      canFill={false}
                      canFit={false}
                      image={properties.image || undefined}
                      altText={properties.altText}
                      sourceUrl={properties.sourceUrl}
                      closeModal={closeModal}
                      onUpdateData={data => {
                        const image = data.image ? data.image : null;
                        updateComponentProperties({
                          input: {
                            sectionId,
                            componentId: id,
                            componentData: {
                              properties: {
                                altText: data.altText,
                                sourceUrl: data.sourceUrl,
                                image: image
                                  ? {
                                      fileName: image.fileName,
                                      key: image.key,
                                      imageType: 'content',
                                      data: {
                                        flip: image.data?.flip || null,
                                      },
                                    }
                                  : null,
                              },
                            },
                          },
                          typename: 'ActivitiesDrawComponent',
                          isSaveImmediate: true,
                          onCompleted: () => closeModal(),
                        });
                      }}
                    />
                  )}
                  <SettingsGroup>
                    <SettingsGroup as="fieldset">
                      <SettingsLabel as="legend">
                        Background Image
                      </SettingsLabel>
                      <Box $marginBottom={16}>
                        {image ? (
                          <ComponentImageField
                            image={
                              properties.image
                                ? {
                                    key: properties.image.key,
                                    fileName: properties.image.fileName,
                                    original: properties.image.original,
                                    flip: properties.image.data?.flip,
                                    alt: properties.altText,
                                  }
                                : null
                            }
                            ariaLabel="Edit background image"
                            onEdit={openModal}
                          />
                        ) : (
                          <FileDropperContextProvider>
                            <ImageUploadField
                              image={image}
                              elementDimensionLabel={elementDimensionLabel}
                              onImageUpload={handleImageUpload}
                            />
                          </FileDropperContextProvider>
                        )}
                      </Box>
                      <SettingsGroup>
                        <SecondaryButton
                          type="button"
                          aria-haspopup="true"
                          onClick={openCustomDraw}
                        >
                          Customise Background
                        </SecondaryButton>
                      </SettingsGroup>
                    </SettingsGroup>
                  </SettingsGroup>
                </ActivityBuilderDrawerContent>
              </ActivityBuilderDrawerWrapper>
            )}
          />
          {isCustomDrawVisible && (
            <EditDrawCanvas
              isVisible={isCustomDrawVisible}
              id={`${id}-custom-background`}
              type="custom-background"
              imageUrl={properties.image?.original}
              drawingData={properties.draw || undefined}
              dimensionsData={
                properties.dimensionData || { width: 800, height: 600 }
              }
            >
              {({ canvasContent, getCanvasData }) => (
                <ActivitySheet
                  isVisible
                  title="Customise Background"
                  customActions={
                    <>
                      <SecondaryButton
                        type="button"
                        onClick={() => closeCustomDraw()}
                      >
                        Discard Changes
                      </SecondaryButton>
                      <PrimaryButton
                        type="button"
                        onClick={() => handleSaveAndClose(getCanvasData())}
                      >
                        Save and Close
                      </PrimaryButton>
                    </>
                  }
                  banner={
                    <ActivitySampleBanner>
                      <PStyled marginBottom={0}>
                        You are in <strong>edit mode</strong>. Any updates made
                        here will be locked in present mode and treated as part
                        of the image background.
                      </PStyled>
                    </ActivitySampleBanner>
                  }
                >
                  <DrawSettingsCanvasWrapper>
                    {canvasContent}
                  </DrawSettingsCanvasWrapper>
                </ActivitySheet>
              )}
            </EditDrawCanvas>
          )}
        </>
      )}
    </MultiView>
  );
};

export default DrawSettings;
